import { cargosDe, puede, type Usuario } from "@/features/acceso/api";
import { alcanzaLaLinea } from "@/features/produccion/lineas";

import { navigation, type NavChild, type NavItem } from "./navigation";

/**
 * **Qué secciones y pestañas ve cada persona**: una sola regla para el menú lateral y para el buscador
 * de la barra (Ctrl+K). Si cada uno filtrara a su manera, el buscador ofrecería una puerta que el menú
 * esconde —o al revés— y las dos acabarían diciendo cosas distintas.
 *
 * No se enseña una puerta que da a un 403. Mientras la API no mande `permissions`, `puede` deja pasar
 * todo: la pantalla no se adelanta a negar lo que el servidor todavía permite.
 *
 * **Una sección se enseña si se puede abrir algo de dentro**, no solo si se tiene su permiso. José
 * prepara el color: tiene `color.ver` y `color.preparar`, y **no** tiene `produccion.ver`. Producción
 * entera desaparecía de su menú, así que **su propia pantalla** solo se alcanzaba escribiendo la
 * dirección a mano. Se mira si alguna pestaña pide un permiso que sí tiene. Las pestañas **sin** permiso
 * propio no cuentan: esas heredan el de la sección, y contarlas abriría la puerta de par en par.
 */
export function seccionesVisibles(usuario: Usuario | null | undefined): NavItem[] {
  const puedeEntrar = (p?: string) => !p || puede(usuario, p);
  return navigation.filter(
    (item) =>
      puedeEntrar(item.permission) ||
      (item.alternativePermission != null && puede(usuario, item.alternativePermission)) ||
      (item.children ?? []).some((c) => c.permission && puede(usuario, c.permission)),
  );
}

/**
 * **Una pestaña se enseña si se tiene su permiso y, si es una línea, si el cargo alcanza a ella.**
 *
 * Un cargo puede estar limitado a algunas líneas de producción (0139): «aquí solo quiero asignar la
 * producción de Cerámicos». `/auth/me` lo trae en `production_lines` —`null` = todas— y el menú
 * enseña solo esas. Quien lo hace cumplir es el servidor; esto evita la puerta que da a un 403. Una entrada con
 * `lines` (el tablero único) se ve si alcanza cualquiera de ellas.
 */
export function pestanaVisible(usuario: Usuario | null | undefined): (child: NavChild) => boolean {
  return (c) => (!c.onlyAdmin || Boolean(usuario && cargosDe(usuario).some(role => role.code === "admin")))
    && (!c.permission || puede(usuario, c.permission)) && (!c.line || [c.line, ...(c.lines ?? [])].some((l) => alcanzaLaLinea(l, usuario?.production_lines)));
}
